import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Rocket, Lock, User } from 'lucide-react';

const AdminLogin = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    console.log('🔐 Admin login attempt:', { username: credentials.username, password: '***' });
    
    if (!credentials.username || !credentials.password) {
      toast.error('Please enter username and password');
      return;
    }

    setLoading(true);
    try {
      console.log('📡 Sending admin login request...');
      const response = await axios.post(`${API}/auth/admin/login`, credentials, {
        withCredentials: true
      });
      
      console.log('✓ Admin login response received:', response.data.user.name);
      
      // Store session token
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
        console.log('✓ Session token saved');
      }
      
      onLoginSuccess(response.data.user);
      toast.success('Admin login successful');
      navigate('/admin');
    } catch (error) {
      console.error('❌ Admin login error:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || 'Login failed';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-6">
      <Card className="w-full max-w-md bg-slate-800/40 border-slate-700 p-8" data-testid="admin-login-page">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-700 rounded-lg flex items-center justify-center">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <Rocket className="w-5 h-5 text-cyan-400" />
                <span className="text-lg font-bold text-slate-100">StartupBlackbox</span>
              </div>
              <p className="text-xs text-red-400">Admin Access</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <Label htmlFor="username" className="text-slate-300">Username</Label>
            <div className="relative mt-1">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                id="username"
                data-testid="admin-username-input"
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                className="bg-slate-800 border-slate-700 text-slate-100 pl-10"
                placeholder="Enter username"
                autoComplete="username"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password" className="text-slate-300">Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                id="password"
                data-testid="admin-password-input"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="bg-slate-800 border-slate-700 text-slate-100 pl-10"
                placeholder="Enter password"
                autoComplete="current-password"
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="w-full bg-gradient-to-r from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 text-white"
          >
            {loading ? 'Logging in...' : 'Admin Login'}
          </Button>
        </form>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            data-testid="back-to-home-btn"
            className="text-slate-400 hover:text-cyan-400 text-sm"
          >
            ← Back to Home
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default AdminLogin;
